// features/admin/chat/AdminChatPanel.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { 
  MessageCircle, 
  Users, 
  ShoppingBag, 
  Filter, 
  Search, 
  Eye, 
  Clock,
  Mail,
  Phone,
  Building2,
  CheckCircle,
  XCircle,
  MoreVertical,
  Download,
  Archive
} from 'lucide-react';

const AdminChatPanel = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'archived', 'unread'
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    unread: 0,
    today: 0
  });

  // Subscribe to all chats in real-time
  useEffect(() => {
    setLoading(true);
    
    const chatsRef = collection(db, 'chats');
    let q = query(chatsRef, orderBy('lastMessageTime', 'desc'));
    
    if (filter !== 'all') {
      q = query(chatsRef, where('status', '==', filter), orderBy('lastMessageTime', 'desc'));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chatsData = [];
      let totalUnread = 0;
      let activeChats = 0;
      let todayChats = 0;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      snapshot.forEach((doc) => {
        const data = doc.data();
        const chat = {
          id: doc.id,
          ...data,
          lastMessageTime: data.lastMessageTime?.toDate?.() || new Date(),
          createdAt: data.createdAt?.toDate?.() || new Date()
        };
        
        // Calculate unread messages
        const unread = (chat.buyerUnread || 0) + (chat.sellerUnread || 0);
        totalUnread += unread;
        
        // Count active chats
        if (chat.status === 'active') activeChats++;
        
        // Count today's chats
        const chatDate = chat.lastMessageTime;
        if (chatDate >= today) todayChats++;
        
        chatsData.push(chat);
      });
      
      setChats(chatsData);
      setStats({
        total: chatsData.length,
        active: activeChats,
        unread: totalUnread,
        today: todayChats
      });
      setLoading(false);
    }, (error) => {
      console.error('Error fetching chats:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [filter]);

  // Subscribe to messages when a chat is selected
  useEffect(() => {
    if (!selectedChat) return;

    const messagesRef = collection(db, 'chats', selectedChat.id, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        messagesData.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.() || new Date()
        });
      });
      setMessages(messagesData);
    });

    return () => unsubscribe();
  }, [selectedChat]);

  // Filter chats based on search term
  const filteredChats = chats.filter(chat => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      chat.productName?.toLowerCase().includes(searchLower) ||
      chat.buyerName?.toLowerCase().includes(searchLower) ||
      chat.sellerName?.toLowerCase().includes(searchLower) ||
      chat.buyerEmail?.toLowerCase().includes(searchLower) ||
      chat.sellerEmail?.toLowerCase().includes(searchLower) ||
      chat.lastMessage?.toLowerCase().includes(searchLower)
    );
  });

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    
    if (now.toDateString() === date.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const sendAdminMessage = async () => {
    if (!selectedChat || !newMessage.trim()) return;

    try {
      const messageData = {
        senderId: 'admin',
        senderName: 'Admin Support',
        senderRole: 'admin',
        message: newMessage,
        type: 'text',
        timestamp: serverTimestamp(),
        readBy: ['admin']
      };

      const messageRef = doc(db, 'chats', selectedChat.id, 'messages', `admin_${Date.now()}`);
      await setDoc(messageRef, messageData);

      // Update chat metadata
      const chatRef = doc(db, 'chats', selectedChat.id);
      await updateDoc(chatRef, {
        lastMessage: newMessage,
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setNewMessage('');
    } catch (error) {
      console.error('Error sending admin message:', error);
    }
  };

  const updateChatStatus = async (chatId, status) => {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating chat status:', error);
    }
  };

  const markAllAsRead = async (chatId) => {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        buyerUnread: 0,
        sellerUnread: 0,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const exportChat = (chat) => {
    const chatData = {
      ...chat,
      messages: messages.filter(msg => msg.chatId === chat.id)
    };
    
    const dataStr = JSON.stringify(chatData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `chat_${chat.id}_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  if (loading && chats.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar - Chat List */}
      <div className="w-96 border-r bg-white flex flex-col">
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Admin Chat Panel</h1>
            <div className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
              {stats.total} chats
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filters */}
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-lg ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-3 py-1.5 text-sm rounded-lg ${filter === 'active' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Active
            </button>
            <button
              onClick={() => setFilter('archived')}
              className={`px-3 py-1.5 text-sm rounded-lg ${filter === 'archived' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Archived
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs text-gray-600">Total Chats</p>
              <p className="text-lg font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-xs text-gray-600">Unread</p>
              <p className="text-lg font-bold text-gray-900">{stats.unread}</p>
            </div>
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          {filteredChats.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p>No chats found</p>
            </div>
          ) : (
            filteredChats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => setSelectedChat(chat)}
                className={`p-4 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedChat?.id === chat.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {chat.productName}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {formatTime(chat.lastMessageTime)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 truncate">
                      {chat.buyerName} → {chat.sellerName}
                    </p>
                    
                    <p className="text-sm text-gray-700 mt-1 truncate">
                      {chat.lastMessage}
                    </p>
                    
                    {/* Status and Unread badges */}
                    <div className="flex items-center space-x-2 mt-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        chat.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {chat.status}
                      </span>
                      
                      {(chat.buyerUnread > 0 || chat.sellerUnread > 0) && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                          {chat.buyerUnread + chat.sellerUnread} unread
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Chat Details */}
      <div className="flex-1 flex flex-col">
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{selectedChat.productName}</h2>
                  <div className="flex items-center space-x-4 mt-1">
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-3 w-3 mr-1" />
                      <span className="font-medium">Buyer:</span> {selectedChat.buyerName}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Building2 className="h-3 w-3 mr-1" />
                      <span className="font-medium">Seller:</span> {selectedChat.sellerName}
                    </div>
                    <div className={`px-2 py-0.5 text-xs rounded-full ${
                      selectedChat.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedChat.status}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => markAllAsRead(selectedChat.id)}
                    className="px-4 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                  >
                    Mark Read
                  </button>
                  
                  <button
                    onClick={() => exportChat(selectedChat)}
                    className="px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Export
                  </button>
                  
                  <button
                    onClick={() => updateChatStatus(selectedChat.id, 
                      selectedChat.status === 'active' ? 'archived' : 'active'
                    )}
                    className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    {selectedChat.status === 'active' ? 'Archive' : 'Activate'}
                  </button>
                </div>
              </div>
              
              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4 mt-4 p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-600">Buyer Contact</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Mail className="h-3 w-3 text-gray-500" />
                    <span className="text-sm">{selectedChat.buyerEmail}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Seller Contact</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Mail className="h-3 w-3 text-gray-500" />
                    <span className="text-sm">{selectedChat.sellerEmail}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
              {messages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p>No messages yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.senderRole === 'admin' ? 'justify-center' :
                        message.senderRole === 'buyer' ? 'justify-start' :
                        'justify-end'
                      }`}
                    >
                      <div className={`max-w-lg rounded-lg p-3 ${
                        message.senderRole === 'admin' 
                          ? 'bg-yellow-100 border border-yellow-200' 
                          : message.senderRole === 'buyer'
                          ? 'bg-blue-100'
                          : 'bg-green-100'
                      }`}>
                        <div className="flex justify-between items-start mb-1">
                          <div>
                            <span className="font-semibold text-sm">
                              {message.senderName}
                            </span>
                            <span className="ml-2 text-xs text-gray-600">
                              ({message.senderRole})
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatMessageTime(message.timestamp)}
                          </span>
                        </div>
                        
                        <p className="text-gray-800">{message.message}</p>
                        
                        {/* Show product details for inquiry messages */}
                        {message.type === 'inquiry' && message.productDetails && (
                          <div className="mt-2 p-2 bg-white bg-opacity-50 rounded text-xs">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <strong>Product:</strong> {message.productDetails.productName}
                              </div>
                              <div>
                                <strong>Quantity:</strong> {message.productDetails.quantity}
                              </div>
                              <div>
                                <strong>Unit Price:</strong> ₹{message.productDetails.unitPrice}
                              </div>
                              <div>
                                <strong>Total:</strong> ₹{message.productDetails.totalPrice}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t bg-white">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendAdminMessage()}
                  placeholder="Type a message as admin..."
                  className="flex-1 border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={sendAdminMessage}
                  disabled={!newMessage.trim()}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Select a chat to view messages</p>
              <p className="text-sm text-gray-500 mt-2">
                Monitor conversations between buyers and sellers
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminChatPanel;